import type { Degree, DegreeData, DegreeProgress, Requirement, RequirementProgress } from '@/app/types/interfaces';
import { calculateDegreeProgress, calculateRequirementProgress, createCalculationContext, expandCoursePools, type DegreeCalculationContext } from '@/app/services/degreeCalculationService';
import { isLikelyCourseCode, normalize } from '@/app/services/degreeRequirementService';

export const DEGREE_WORKSPACE_STORAGE_VERSION = '5';
export type DegreeRequirementFilter = 'all' | 'completed' | 'remaining' | 'courses';
export type DegreeWorkspaceUrl = { query: string; degreeId: number | null };

export function degreeWorkspaceUrlFrom(params: Pick<URLSearchParams, 'get'>): DegreeWorkspaceUrl {
  const query = (params.get('q') ?? '').trim();
  const rawId = params.get('degree') ?? params.get('selectDegreeId');
  const numericId = rawId && /^\d+$/.test(rawId) ? Number(rawId) : NaN;
  return { query, degreeId: Number.isSafeInteger(numericId) && numericId > 0 ? numericId : null };
}

/** Safe, non-control query fields may survive the legacy redirect. Tokens and auth
 * callback fields intentionally do not become part of a public Explore URL. */
export function preserveDegreeWorkspaceQuery(params: URLSearchParams): URLSearchParams {
  const preserved = new URLSearchParams();
  for (const [key, value] of params) {
    if ((key === 'term' || key === 'semester') && value.length <= 80 && !/[\u0000\r\n]/.test(value)) preserved.append(key, value);
  }
  return preserved;
}

export function degreeWorkspaceUrlTo(value: DegreeWorkspaceUrl, preserved = new URLSearchParams()): string {
  const params = preserveDegreeWorkspaceQuery(preserved); params.set('view', 'degrees');
  if (value.query.trim()) params.set('q', value.query.trim());
  if (value.degreeId && value.degreeId > 0) params.set('degree', String(value.degreeId));
  return `/explore?${params.toString()}`;
}

/** Integration adapter for the future /degree-search compatibility redirect. */
export function legacyDegreeSearchRedirect(params: URLSearchParams): string {
  return degreeWorkspaceUrlTo(degreeWorkspaceUrlFrom(params), params);
}

export function filterDegrees(degrees: Degree[], query: string): Degree[] {
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return degrees;
  return degrees.filter((degree) => {
    const name = String(degree.Name ?? '').toLowerCase();
    const code = String(degree['Degree Code'] ?? '').toLowerCase();
    const haystack = `${name} ${code}`;
    return terms.every((term) => {
      if (haystack.includes(term)) return true;
      const campusCode = term === 'camden' ? 'ca' : term === 'newark' ? 'nk' : null;
      return campusCode !== null && (name.includes(`(${campusCode})`) || code.startsWith(campusCode));
    });
  });
}

/** Share the same campus-aware result order with the dashboard entry point. */
export function rankDegreeMatches(degrees: Degree[], query: string): Degree[] {
  const normalizedQuery = query.trim().toLowerCase();
  const campusAsked = /\b(camden|newark)\b|\((ca|nk)\)/.test(normalizedQuery);
  const searchableName = (name: string) => name.replace(/^(degree audit for\s+)?(major|minor|certificate|certification)\s+in\s+/, '');
  const score = (name: string) => !normalizedQuery ? 0 : name === normalizedQuery ? 0
    : name.startsWith(normalizedQuery) ? 1 : name.includes(` ${normalizedQuery}`) ? 2 : 3;
  return [...filterDegrees(degrees, normalizedQuery)].sort((left, right) => {
    const leftName = searchableName(left.Name.toLowerCase());
    const rightName = searchableName(right.Name.toLowerCase());
    const campusRank = (name: string) => campusAsked || !/\((ca|nk)\)/.test(name) ? 0 : 1;
    return campusRank(leftName) - campusRank(rightName)
      || score(leftName) - score(rightName)
      || (normalizedQuery ? leftName.length - rightName.length : 0)
      || leftName.localeCompare(rightName) || left.ID - right.ID;
  });
}

export type DegreeCategory = 'majors' | 'minors' | 'certificates' | 'other';
export function categorizeDegrees(degrees: Degree[]): Record<DegreeCategory, Degree[]> {
  const groups: Record<DegreeCategory, Degree[]> = { majors: [], minors: [], certificates: [], other: [] };
  for (const degree of degrees) {
    const name = `${degree.Name ?? ''} ${degree['Degree Code'] ?? ''}`.toLowerCase();
    const category: DegreeCategory = /core curriculum|general education/.test(name) ? 'other'
      : /minor/.test(name) ? 'minors' : /certificate|certification/.test(name) ? 'certificates'
      : /major|bachelor|\bbs\b|\bba\b|degree audit/.test(name) ? 'majors' : 'other';
    groups[category].push(degree);
  }
  (Object.keys(groups) as DegreeCategory[]).forEach((key) => groups[key].sort((a, b) => a.Name.localeCompare(b.Name)));
  return groups;
}

export function rankClosestDegrees(degrees: Degree[], progress: Map<number, DegreeProgress>): Degree[] {
  return [...degrees].filter((degree) => progress.has(degree.ID)).sort((a, b) => {
    const aProgress = progress.get(a.ID)!; const bProgress = progress.get(b.ID)!;
    return bProgress.creditProgress - aProgress.creditProgress || bProgress.overallProgress - aProgress.overallProgress || a.ID - b.ID;
  });
}

export function mergeStarredDegreePreferences(current: unknown, stars: number[]): Record<string, unknown> {
  const preferences = current && typeof current === 'object' && !Array.isArray(current) ? current as Record<string, unknown> : {};
  return { ...preferences, starredDegrees: stars };
}

export function appendDegreeToPlan(plan: unknown, degreeId: number): Record<string, unknown> | null {
  if (!plan || typeof plan !== 'object' || Array.isArray(plan)) return null;
  const value = plan as Record<string, unknown>;
  const existing = Array.isArray(value.selectedDegreeIds) ? value.selectedDegreeIds.filter((id): id is number => Number.isSafeInteger(id) && id > 0) : [];
  return { ...value, selectedDegreeIds: Array.from(new Set([...existing, degreeId])) };
}

export type CourseDisplay = { kind: 'course'; code: string; name?: string; completed: boolean };
export type CourseListDisplay = { kind: 'list'; key: string; label: string; missing: boolean; courses: CourseDisplay[] };
export type RequirementNodeView = { id: string; title: string; description?: string; coursesNeeded?: number; creditsNeeded?: number | null; logic: 'and' | 'or'; status: RequirementProgress['status']; progress: number; visible: boolean; children: RequirementNodeView[]; entries: Array<CourseDisplay | CourseListDisplay> };

export function filterRequirementTree(nodes: RequirementNodeView[], filter: DegreeRequirementFilter): RequirementNodeView[] {
  if (filter === 'all') return nodes;
  return nodes.map((node) => {
    const children = filterRequirementTree(node.children, filter);
    const ownMatch = filter === 'courses' ? node.entries.some((entry) => entry.kind === 'course' || entry.courses.length > 0) : filter === 'completed' ? node.status === 'completed' : node.status !== 'completed';
    return { ...node, children, visible: ownMatch || children.length > 0 };
  }).filter((node) => node.visible);
}

function requirementEntries(requirement: Requirement, progress: RequirementProgress, lists: Map<string, { classes: string[]; name?: string }>, names: Map<string, string>): Array<CourseDisplay | CourseListDisplay> {
  const completed = new Set(progress.completedCourses.map(normalize));
  return (requirement.course_pool ?? []).map((entry) => {
    if (!lists.has(entry) && isLikelyCourseCode(entry)) return { kind: 'course' as const, code: entry, name: names.get(normalize(entry)), completed: completed.has(normalize(entry)) };
    const list = lists.get(entry);
    return { kind: 'list' as const, key: entry, label: list?.name ?? entry, missing: !list, courses: (list?.classes ?? []).map((code) => ({ kind: 'course' as const, code, name: names.get(normalize(code)), completed: completed.has(normalize(code)) })) };
  });
}

export function requirementTreeView(requirements: Requirement[] | undefined, context: DegreeCalculationContext, lists: Map<string, { classes: string[]; name?: string }>, names: Map<string, string>, filter: DegreeRequirementFilter): RequirementNodeView[] {
  const build = (requirement: Requirement): RequirementNodeView => {
    const progress = calculateRequirementProgress(requirement, context);
    const children = (requirement.requirements ?? []).map(build);
    const matches = filter === 'all' || (filter === 'courses' ? ((requirement.course_pool?.length ?? 0) > 0) : filter === 'completed' ? progress.status === 'completed' : progress.status !== 'completed');
    const visible = matches || children.some((child) => child.visible);
    // Repeated source wrappers add no obligation. Keep their shared progress while
    // exposing the actual description and quota on the collapsed topic header.
    if (!requirement.course_pool?.length && !requirement.courses_needed && !requirement.credits_needed && !requirement.description?.trim() && children.length === 1 && requirement.title?.trim().toLowerCase() === children[0].title.trim().toLowerCase()) {
      return { ...children[0], id: requirement.id, status: progress.status, progress: progress.progress, visible };
    }
    return { id: requirement.id, title: requirement.title ?? requirement.id, description: requirement.description, coursesNeeded: requirement.courses_needed, creditsNeeded: requirement.credits_needed, logic: requirement.logic ?? 'and', status: progress.status, progress: progress.progress, visible, children, entries: requirementEntries(requirement, progress, lists, names) };
  };
  return (requirements ?? []).map(build).filter((node) => node.visible);
}

export type DegreeInspectorModel = { degree: Degree; progress: DegreeProgress | null; tree: RequirementNodeView[]; minimumSummary: string | null };
export function hasCapturedRequirements(requirements: DegreeData | Requirement[] | null | undefined): boolean {
  const rows = Array.isArray(requirements) ? requirements : requirements?.requirements;
  return Array.isArray(rows) && rows.length > 0;
}

export function createDegreeInspectorModel(degree: Degree, requirements: DegreeData | Requirement[] | undefined, prerequisites: DegreeCalculationContext['userPrerequisites'], lists: Map<string, { classes: string[]; name?: string; description?: string }>, credits: Map<string, number>, names: Map<string, string>, filter: DegreeRequirementFilter, includeEmptyCoursework = false): DegreeInspectorModel {
  const requirementList = Array.isArray(requirements) ? requirements : requirements?.requirements;
  const context = createCalculationContext(prerequisites, expandCoursePools(requirements, lists), credits, {
    degreeCourseListsDirect: lists,
    courseNameMap: names,
  });
  const progress = hasCapturedRequirements(requirements) && (prerequisites.length > 0 || includeEmptyCoursework)
    ? calculateDegreeProgress(degree, requirements, context, { includeMinimumCourses: true }) : null;
  return { degree, progress, tree: requirementTreeView(requirementList, context, lists, names, filter), minimumSummary: progress?.minimumCoursesNeeded === undefined ? null : `${progress.minimumCoursesNeeded} course${progress.minimumCoursesNeeded === 1 ? '' : 's'} in the current minimum-course estimate` };
}

export function degreeCampusLabel(degree: Degree): string {
  const campus = /\((NB|NK|CA)\)/i.exec(degree.Name)?.[1]?.toUpperCase() ?? String(degree['Degree Code'] ?? '').slice(0, 2).toUpperCase();
  return ({ NB: 'New Brunswick', NK: 'Newark', CA: 'Camden' } as Record<string, string>)[campus] ?? 'Campus not specified';
}
